import React, { useRef, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useCurrency } from "./utility/CurrencyContext";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import invoice_logo from "../assets/invoice_logo.png";
import axios from "axios";
import config from '../config';

const ThankYouPage = () => {
    const [searchParams] = useSearchParams();
    const { getCurrencySymbol, convertPrice, convertThankyouPrice } = useCurrency();
    const invoiceRef = useRef();

    // State to store fetched data
    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const status = searchParams.get("status");
        const orderId = searchParams.get("order_id");
        const message = searchParams.get("message");

        if (status === "success" && orderId) {
            // Fetch order details from the backend
            const fetchOrderDetails = async () => {
                try {
                    const response = await axios.get(
                        `${config.baseURL}/get_order_details/${orderId}`
                    );
                    // Check if the response data is valid
                    if (response.data && response.data.personalInfo && response.data.cartItems) {
                        setOrderData(response.data);
                    } else {
                        setError("Received invalid order data from the server.");
                    }
                } catch (err) {
                    setError("Failed to load order details. Please try again later.");
                    console.error("API Fetch Error:", err);
                } finally {
                    setLoading(false);
                }
            };
            fetchOrderDetails();
        } else if (status === "failure") {
            setError(message || "Payment failed or was not verified.");
            setLoading(false);
        } else {
            setError("No order status found.");
            setLoading(false);
        }
    }, [searchParams]);

    const handleDownloadPdf = async () => {
        if (!invoiceRef.current || !orderData) {
            alert("Order data is not yet loaded. Please wait.");
            return;
        }

        const input = invoiceRef.current;
        const options = {
            scale: 2,
            useCORS: true,
            logging: true,
            scrollX: 0,
            scrollY: -window.scrollY,
        };

        try {
            const canvas = await html2canvas(input, options);
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");

            const imgWidth = 210;
            const pageHeight = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            pdf.save(`invoice_${orderData.order_id || "guest"}_${Date.now()}.pdf`);
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Failed to generate PDF. Please try again.");
        }
    };

    // Conditional Rendering: This is the most crucial part
    if (loading) {
        return (
            <div className="container mx-auto p-8 text-center">
                <h1 className="text-3xl font-bold text-gray-800 mb-4">Loading Order Details...</h1>
                <p className="text-lg text-gray-600">Please wait while we confirm your payment.</p>
            </div>
        );
    }

    if (error || !orderData) {
        return (
            <div className="container mx-auto p-8 text-center">
                <h1 className="text-3xl font-bold text-red-600 mb-4">Payment Failed!</h1>
                <p className="text-lg text-gray-600 mb-4">
                    There was an issue processing your order.
                </p>
                <p className="text-red-500 mt-4">{error || "No order data available."}</p>
                <Link
                    to="/products2/20"
                    className="mt-6 inline-block bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700"
                >
                    Continue Shopping
                </Link>
            </div>
        );
    }

    // Now it's safe to destructure and use orderData
    const { personalInfo, cartItems, amount } = orderData;
    const invoiceNumber = searchParams.get("invoice_no");
    const invoiceDate = new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

    // Use the currency from personalInfo for all calculations
    const orderCurrency = personalInfo.currency;
    
    // Calculations based on fetched data
    const subtotal = cartItems.reduce((sum, item) => {
      const price = parseFloat(convertThankyouPrice(item.unit_price, orderCurrency)) || 0;
      
      return sum + (price * item.quantity);
    }, 0);

    const vatRate = 0.18; // Assuming 18% VAT
    const vat = subtotal * vatRate;
    const total = subtotal + vat;

    return (
        <div className="container mx-auto p-8 max-w-4xl">
            {/* Download Button */}
            <div className="text-center mb-8 gap-2 flex">
                <Link
                    to="/products2/20"
                    className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700"
                >
                    Continue Shopping
                </Link>
                <button
                    onClick={handleDownloadPdf}
                    className="bg-green-600 text-white px-6 py-3 rounded-md shadow-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75"
                >
                    Download (PDF)
                </button>

            </div>
            {/* <div className="text-center mt-8">
                
            </div> */}

            {/* Invoice Container - this is the part that will be converted to PDF */}
            <div
                id="invoice-content"
                ref={invoiceRef}
                className="bg-white shadow-xl rounded-lg p-8 border border-gray-200 mb-[5rem]"
            >
                <div className="flex justify-between items-start mb-8 border-b pb-4">
                    <div className="text-left">
                        <h2 className="text-2xl font-bold text-gray-800">Super Springs</h2>{" "}
                        <p className="text-gray-600">
                            D-235, Sector-10, Noida (UP) Pin Code: 201301 INDIA
                        </p>{" "}
                        <p className="text-gray-600">
                            <strong>Phone: </strong>+91 120 4337718
                        </p>{" "}
                        <p className="text-gray-600">
                            <strong>Mobile No.: </strong> +91 98104 60594 , +91 9810117900
                        </p>{" "}
                        <p className="text-gray-600">
                            <strong>Email: </strong>info@supersprings.co.in
                        </p>{" "}
                    </div>
                    <div className="flex flex-col justify-center items-center">
                        <img src={invoice_logo} alt="Super Springs Logo" className="w-40" />
                        <p className="text-lg text-gray-600">Order Confirmation</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">
                    <div>
                        <p className="text-gray-700 font-semibold mb-2">Order To:</p>
                        <p className="text-gray-700">
                            <strong>Name: </strong>
                            {personalInfo.firstName} {personalInfo.lastName}
                        </p>
                        <p className="text-gray-700">
                            <strong>Email: </strong> {personalInfo.email}
                        </p>
                        <p className="text-gray-700">
                            <strong>Phone: </strong> {personalInfo.phone}
                        </p>
                        <p className="text-gray-700">
                            <strong>Country: </strong> {personalInfo.country}
                        </p>
                        <p className="text-gray-700">
                            <strong>City: </strong>
                            {personalInfo.town}, {personalInfo.postalCode}
                        </p>
                        <p className="text-gray-700">
                            <strong>Address: </strong>
                            {personalInfo.address}
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-gray-700 font-semibold mb-2">Order Details:</p>
                        <p className="text-gray-700">
                            <strong>Order No:</strong>{" "}
                            <span className="font-mono text-blue-600">
                                {invoiceNumber}
                            </span>
                        </p>
                        <p className="text-gray-700">
                            <strong>Order Date:</strong> {invoiceDate}
                        </p>
                        {/* <p className="text-gray-700">
                            <strong>Order ID:</strong>{" "}
                            <span className="font-mono text-blue-600">
                                {searchParams.get("order_id")}
                            </span>
                        </p> */}
                    </div>
                </div>

                {/* Ordered Products Table */}
                <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-700 mb-4">
                        Order Items
                    </h2>
                    <div className="overflow-x-auto border rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        Product Name
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        Unit
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        Quantity
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        Unit Price
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-right"
                                    >
                                        Line Total
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {cartItems.map((product, index) => {
                                    const unit = parseFloat(convertThankyouPrice(product.unit_price, orderCurrency)) || 0;
                                    const lineTotal = product.quantity * unit;
                                    return (
                                        <tr key={index}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {product.product_name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {personalInfo.unit}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {product.quantity}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {getCurrencySymbol(orderCurrency)}{unit.toFixed(2)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                                {getCurrencySymbol(orderCurrency)}{lineTotal.toFixed(2)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Order Summary */}
                <div className="flex justify-end">
                    <div className="w-full sm:w-1/2 md:w-1/3">
                        <h2 className="text-xl font-semibold text-gray-700 mb-4">
                            Order Summary
                        </h2>
                        <div className="flex justify-between items-center text-gray-700 mb-2">
                            <span>Subtotal:</span>
                            <span>
                                {getCurrencySymbol(orderCurrency)}
                                {subtotal.toFixed(2)}
                            </span>
                        </div>
                        <div className="flex justify-between items-center text-gray-700 mb-2">
                            <span>GST (18%):</span>
                            <span>
                                {getCurrencySymbol(orderCurrency)}
                                {vat.toFixed(2)}
                            </span>
                        </div>
                        <div className="flex justify-between items-center text-gray-800 font-bold text-xl border-t pt-2 mt-2">
                            <span>Total:</span>
                            <span>
                                {getCurrencySymbol(orderCurrency)}
                                {total.toFixed(2)}
                            </span>
                        </div>
                    </div>
                </div>

                {personalInfo.notes && (
                    <div className="mt-8 border-t pt-4">
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">
                            Order Note:
                        </h3>
                        <p className="text-gray-600">{personalInfo.notes}</p>
                    </div>
                )}

                <div className="text-center mt-12 text-gray-500 text-sm">
                    <p>Thank you for your business!</p>
                </div>
            </div>
        </div>
    );
};

export default ThankYouPage;